import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@components/atoms/button';
import { Typography } from '@components/atoms/typography';
import { InfoIcon } from '@components/icons';
import { Autocomplete } from '@components/moleculs/autocomplete';
import { useCountryAutocomplete } from '@components/moleculs/autocomplete/hooks/use-country-autocomplete';
import { LabeledField } from '@components/moleculs/labeled-field';
import { TextField } from '@components/moleculs/text-field';
import { useCountryOptions } from '@hooks/options/use-country-options';
import { useToast } from '@hooks/use-toast';
import {
    CameraVariantLineEnum,
    DocsVerificationSideEnum,
    DocsVerificationStepEnum,
    DocsVerificationTabEnum,
    DocsVerificationTypeEnum,
    OrganizationRoleEnum,
} from '@models/enums';
import { DocsVerificationFileUploadApproachEnum } from '@models/enums/docs-verification/docs-verification-file-upload-approach-enum';
import type { AutocompleteOption, FileWithPreview, ThemeProps } from '@models/types';
import { useDemoData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { RequirementTypeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/requirements/types';
import { IMAGE_EXTENSIONS } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/documents/constants';
import { useSetCountrySDKMutation } from '@modules/docs-verification-widget/api';
import { useMobileHeight } from '@modules/docs-verification-widget/hooks/use-mobile-height';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { isValidExtension } from '@utils/files/util';
import { getEnvVar } from '@utils/get-env-var';
import { getImageUrl } from '@utils/get-image-url';
import cn from 'classnames';
import { isTablet } from 'mobile-device-detect';

import { Card } from '../../card';
import { TakeDocument } from '../../downloads/take-document';
import { TakeDocumentMobile } from '../../downloads/take-document-mobile';
import { usePageTitle } from '../../hooks/use-page-title';
import { useUploadFile } from '../../hooks/use-upload-file';
import { MobileHeader } from '../../mobile-header';
import { Requirements } from '../../requirements';
import { STEPS_MOBILE_TITLE } from '../constants';

import { useGetStepText } from './hooks/use-get-step-text';
import { useNextClick } from './hooks/use-next-click';
import { DocumentsContainer } from './styles';
import type { DocumentsProps } from './types';
import { DocumentsStepEnum } from './types';

const disabledOptionStyle = {
    marginLeft: 8,
    fontSize: 10,
};

// eslint-disable-next-line complexity
export const Documents: React.FC<DocumentsProps> = ({ onSubmit, isSubmitting, onBack }) => {
    const { scrollTop } = useWidgetContext();
    const { uploadFileRef } = useDemoData();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`) || isTablet;
    const { minHeight } = useMobileHeight();
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const isCameraMode = useSelector((state: RootState) => state.docsVerification.isCameraMode);
    const isShowRequirements = useSelector(
        (state: RootState) => state.docsVerification.isShowRequirements,
    );
    const documentsCountry = useSelector(
        (state: RootState) => state.docsVerification.documentsCountry,
    );
    const fileUploadApproach = useSelector(
        (state: RootState) => state.docsVerification.fileUploadApproach,
    );
    const documentsStep = useSelector((state: RootState) => state.docsVerification.documentsStep);

    const documentsFiles = useSelector(
        (state: RootState) => state.docsVerification.documents.files,
    ).filter((item) => item.tab === DocsVerificationTabEnum.DOCUMENT);

    const userRole = useSelector(
        (state: RootState) => state.auth?.user?.info?.account?.global_role,
    ) as OrganizationRoleEnum;
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [setCountry, { isLoading: isSetCountryLoading }] = useSetCountrySDKMutation();
    const { showToast } = useToast();
    const { uploadFile } = useUploadFile(IMAGE_EXTENSIONS);
    const countryOptions = useCountryOptions();

    const { renderFlagOption, renderInputFlag } = useCountryAutocomplete({
        disabledOptionSuffix: (
            <span style={{ ...disabledOptionStyle, color: theme.palette.textMain }}>
                {t('common.notSupported')}
            </span>
        ),
    });
    const country = useSelector((state: RootState) => state.docsVerification.country);

    const { pageTitle } = usePageTitle();
    const { nextClick } = useNextClick({
        documentsStep,
        onSubmit,
    });

    const handleFilesChange = useCallback(
        async (file: FileWithPreview) => {
            const firstFile = documentsFiles?.[0];
            let side: DocsVerificationSideEnum | undefined;

            if (firstFile && firstFile?.status.success) {
                if (firstFile?.side === DocsVerificationSideEnum.BACK) {
                    side = DocsVerificationSideEnum.FRONT;
                } else {
                    side = DocsVerificationSideEnum.BACK;
                }
            }

            const uploadedFile = await uploadFile({
                file,
                documentType: DocsVerificationTypeEnum.POI,
                tab: DocsVerificationTabEnum.DOCUMENT,
                side,
            });

            const newFiles = [
                ...documentsFiles.filter((docFile) => !docFile.status.error),
                uploadedFile,
            ];

            if (
                documentsStep === DocumentsStepEnum.UPLOAD_FIRST_SIDE &&
                uploadedFile.isTwoSideDocument &&
                newFiles.length
            ) {
                dispatch(
                    docsVerificationSlice.actions.setDocumentsStep(
                        DocumentsStepEnum.UPLOAD_SECOND_SIDE,
                    ),
                );
            }

            if (uploadedFile.status.success) {
                const isTwoSide = !!newFiles.find((item) => item.isTwoSideDocument);
                const successDocuments = newFiles.filter((item) => item.status.success);
                const isDifferentSides =
                    Array.from(new Set(successDocuments.map((item) => item.side))).length === 2;

                if (isTwoSide && successDocuments.length === 2 && !isDifferentSides) {
                    setTimeout(() => {
                        dispatch(
                            docsVerificationSlice.actions.changeFile({
                                id: uploadedFile.id as string,
                                file: {
                                    ...uploadedFile,
                                    status: {
                                        ...uploadedFile.status,
                                        loading: false,
                                        success: false,
                                        error: true,
                                        message: t('validation.uploadedDocumentWithSameSide'),
                                    },
                                },
                            }),
                        );
                    });

                    return uploadedFile;
                }

                // if (isTwoSide && successDocuments.length === 2 && isDifferentTypes) {
                //     setTimeout(() => {
                //         dispatch(
                //             docsVerificationSlice.actions.changeFile({
                //                 id: uploadedFile.id as string,
                //                 file: {
                //                     ...uploadedFile,
                //                     status: {
                //                         ...uploadedFile.status,
                //                         loading: false,
                //                         success: false,
                //                         error: true,
                //                         message: t('validation.uploadedDocumentWithDifferentType'),
                //                     },
                //                 },
                //             }),
                //         );
                //     });
                //
                //     return uploadedFile;
                // }

                if (
                    successDocuments.length === 2 ||
                    (successDocuments.length === 1 && !uploadedFile.isTwoSideDocument)
                ) {
                    nextClick(newFiles);
                }
            }

            return uploadedFile;
        },
        [uploadFile, documentsFiles, nextClick, dispatch, t, documentsStep],
    );

    const handleTakePhoto = useCallback(
        (file: FileWithPreview) => {
            dispatch(
                docsVerificationSlice.actions.setFileUploadApproach(
                    DocsVerificationFileUploadApproachEnum.PHOTO,
                ),
            );
            handleFilesChange(file);
        },
        [handleFilesChange, dispatch],
    );

    const handleClose = (isCaptured?: boolean) => {
        if (documentsStep === DocumentsStepEnum.UPLOAD_SECOND_SIDE && documentsFiles.length === 0) {
            dispatch(
                docsVerificationSlice.actions.setDocumentsStep(DocumentsStepEnum.UPLOAD_FIRST_SIDE),
            );
        }

        if (isCameraMode) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(false));
        }

        if (isCaptured || documentsFiles.length) {
            return;
        }

        // comment this to use native camera
        // eslint-disable-next-line no-constant-condition
        if (matchesMobileSm && false) {
            dispatch(docsVerificationSlice.actions.setCurrentStep(DocsVerificationStepEnum.START));
        }
    };

    const handleRequirementsClick = () => {
        scrollTop();
        dispatch(docsVerificationSlice.actions.setIsShowRequirements(true));
    };

    const isFilesLoading = !!documentsFiles.filter((item) => item.status.loading).length;
    const isLoading = isFilesLoading || isSubmitting;
    const isError = !!documentsFiles.filter((item) => item.status.error).length;
    const isSuccess =
        documentsFiles.length >= 1 &&
        documentsFiles.filter((item) => item.status.success).length === documentsFiles.length;
    const isTwoSideDocument = !!documentsFiles.filter((item) => item.isTwoSideDocument).length;
    // const successDocuments = documentsFiles.filter((item) => item.status.success);

    const currentSideToUpload = useMemo(() => {
        const successDocument = documentsFiles.find((doc) => doc.status.success);

        if (successDocument && isTwoSideDocument) {
            return successDocument.side === DocsVerificationSideEnum.BACK
                ? DocsVerificationSideEnum.FRONT
                : DocsVerificationSideEnum.BACK;
        }

        return DocsVerificationSideEnum.MAIN;
    }, [documentsFiles, isTwoSideDocument]);

    const handleCancelClick = () => {
        dispatch(
            docsVerificationSlice.actions.setDocumentsStep(
                isTwoSideDocument
                    ? DocumentsStepEnum.UPLOAD_SECOND_SIDE
                    : DocumentsStepEnum.UPLOAD_FIRST_SIDE,
            ),
        );
    };

    const handleNextClick = () => {
        nextClick(documentsFiles);
    };

    const handleSelectCountryNextClick = async () => {
        if (documentsCountry !== country?.value) {
            try {
                if (!sdkOptions || !country) {
                    return;
                }
                await setCountry({
                    url: sdkOptions.apiUrl,
                    id: sdkOptions.id,
                    country: country.value,
                }).unwrap();
                dispatch(docsVerificationSlice.actions.setDocumentsCountry(country.value));
            } catch (e) {
                showToast({
                    variant: 'error',
                    title: t('common.somethingWentWrong'),
                    message: t('common.pleaseTryAgainLater'),
                });

                return;
            }
        }
        handleNextClick();
    };

    const previews = useMemo(() => {
        const errorFile = documentsFiles.find((item) => item.status.error);

        if (errorFile) {
            const isValid = isValidExtension(errorFile.uploadedFile?.name || '', IMAGE_EXTENSIONS);

            return isValid ? [errorFile.uploadedFile?.preview as string] : [];
        }

        if (
            documentsStep === DocumentsStepEnum.UPLOAD_SECOND_SIDE &&
            documentsFiles[1]?.uploadedFile?.preview
        ) {
            const isValid = isValidExtension(documentsFiles[1].uploadedFile.name, IMAGE_EXTENSIONS);

            return isValid ? [documentsFiles[1].uploadedFile.preview] : [];
        }

        if (
            documentsStep === DocumentsStepEnum.UPLOAD_FIRST_SIDE &&
            documentsFiles?.[0]?.uploadedFile?.preview
        ) {
            const isValid = isValidExtension(documentsFiles[0].uploadedFile.name, IMAGE_EXTENSIONS);

            return isValid ? [documentsFiles[0].uploadedFile.preview] : [];
        }

        return undefined;
    }, [documentsFiles, documentsStep]);

    const handleUploadFileClick = useCallback(async () => {
        fileInputRef.current?.click();
    }, []);

    const handleFileUploadChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files?.[0]) {
                const file = event.target.files[0] as FileWithPreview;

                file.preview = URL.createObjectURL(file);
                const lastFile = documentsFiles[documentsFiles.length - 1];

                if (lastFile?.id && (lastFile?.status?.error || documentsFiles.length === 2)) {
                    dispatch(docsVerificationSlice.actions.deleteFileById(lastFile.id));
                    handleFilesChange(file);
                } else {
                    handleFilesChange(file);
                }
                dispatch(
                    docsVerificationSlice.actions.setFileUploadApproach(
                        DocsVerificationFileUploadApproachEnum.FILE,
                    ),
                );
            }
        },
        [handleFilesChange, dispatch, documentsFiles],
    );

    useEffect(() => {
        if (uploadFileRef) {
            uploadFileRef.current = handleFileUploadChange;
        }
    }, [handleFileUploadChange, uploadFileRef]);

    const stepsMobileTitleNode = window.document.getElementById(STEPS_MOBILE_TITLE);

    const { cameraTitle, continueText, cardMessage } = useGetStepText({
        isSuccess,
        isError,
        documentsFiles,
        documentsStep,
        isTwoSideDocument,
        isCameraMode,
        fileUploadApproach,
    });

    const isShowUploadButton = useMemo(() => {
        if (
            isCameraMode ||
            documentsStep === DocumentsStepEnum.SELECT_COUNTRY ||
            fileUploadApproach === DocsVerificationFileUploadApproachEnum.PHOTO ||
            (!isTwoSideDocument && isSuccess) ||
            (isTwoSideDocument && isSuccess && documentsFiles.length === 2)
        ) {
            return false;
        }

        return (
            !!getEnvVar('REACT_APP_IS_SDK_DEBUG') ||
            userRole === OrganizationRoleEnum.ADMIN ||
            !!sdkOptions?.settings.allow_poi_manual_uploads
        );
    }, [
        documentsStep,
        isCameraMode,
        sdkOptions,
        userRole,
        fileUploadApproach,
        isSuccess,
        isTwoSideDocument,
        documentsFiles,
    ]);

    const cardTitle = useMemo(() => {
        if (
            currentSideToUpload === DocsVerificationSideEnum.MAIN ||
            currentSideToUpload === DocsVerificationSideEnum.FRONT
        ) {
            return t('docVerificationWidget.filesUploader.dragAndDrop.mainPage');
        }

        if (currentSideToUpload === DocsVerificationSideEnum.BACK) {
            return t('docVerificationWidget.filesUploader.dragAndDrop.backSide');
        }

        return undefined;
    }, [currentSideToUpload, t]);

    const cardIcon = useMemo(() => {
        if (
            currentSideToUpload === DocsVerificationSideEnum.MAIN ||
            currentSideToUpload === DocsVerificationSideEnum.FRONT
        ) {
            return getImageUrl('/images/sdk/verification/main-page.png');
        }

        if (currentSideToUpload === DocsVerificationSideEnum.BACK) {
            return getImageUrl('/images/sdk/verification/back-side.png');
        }

        return undefined;
    }, [currentSideToUpload]);

    const uploadButtonVariant = useMemo(() => {
        if (isError) {
            return 'error-secondary';
        }
        if (continueText) {
            return 'secondary';
        }

        return 'primary';
    }, [isError, continueText]);

    return (
        <DocumentsContainer
            style={{
                minHeight,
            }}
        >
            {isShowRequirements ? (
                <Requirements type={RequirementTypeEnum.PASSPORT} onBack={onBack} />
            ) : (
                <React.Fragment>
                    {matchesMobileSm &&
                        stepsMobileTitleNode &&
                        createPortal(
                            <React.Fragment>
                                <MobileHeader
                                    onBack={onBack}
                                    title={
                                        documentsStep === DocumentsStepEnum.SELECT_COUNTRY
                                            ? t('docVerificationWidget.documents.selectTitle')
                                            : pageTitle
                                    }
                                />
                            </React.Fragment>,
                            stepsMobileTitleNode,
                        )}
                    <div className='main'>
                        {(documentsStep === DocumentsStepEnum.UPLOAD_FIRST_SIDE ||
                            documentsStep === DocumentsStepEnum.UPLOAD_SECOND_SIDE) && (
                            <React.Fragment>
                                {isCameraMode && (
                                    <React.Fragment>
                                        {matchesMobileSm ? (
                                            <TakeDocumentMobile
                                                title={cameraTitle}
                                                variant={CameraVariantLineEnum.DOCS}
                                                onClose={handleClose}
                                                onChangeFile={handleTakePhoto}
                                                isUseNativeCamera={true}
                                            />
                                        ) : (
                                            <TakeDocument
                                                title={cameraTitle}
                                                variant={CameraVariantLineEnum.DOCS}
                                                onClose={handleClose}
                                                onChangeFile={handleTakePhoto}
                                            />
                                        )}
                                    </React.Fragment>
                                )}
                                {!isCameraMode && (
                                    <div className='upload-container'>
                                        <Card
                                            title={cardTitle}
                                            description={t(
                                                'docVerificationWidget.filesUploaderDescription',
                                            )}
                                            icon={cardIcon}
                                            previews={previews}
                                            message={cardMessage}
                                            onChangeFile={
                                                sdkOptions?.settings.allow_poi_manual_uploads
                                                    ? (file) => handleFilesChange(file)
                                                    : undefined
                                            }
                                            isLoading={isLoading}
                                            isError={isError}
                                            isSuccess={isSuccess}
                                            onRequirementsClick={handleRequirementsClick}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        )}
                        {documentsStep === DocumentsStepEnum.SELECT_COUNTRY && (
                            <div className='select-country-container'>
                                <Typography
                                    tag='div'
                                    variant='text-bold'
                                    className='alert-container'
                                >
                                    <InfoIcon />
                                    <span>
                                        {t('docVerificationWidget.documents.selectCountryAlert')}
                                    </span>
                                </Typography>
                                <LabeledField
                                    label={t('docVerificationWidget.documents.selectCountry')}
                                    className='country-autocomplete'
                                >
                                    <Autocomplete
                                        value={country}
                                        fullWidth={true}
                                        onChange={(e, value) => {
                                            dispatch(
                                                docsVerificationSlice.actions.setCountry(
                                                    value as AutocompleteOption,
                                                ),
                                            );
                                        }}
                                        isOptionEqualToValue={(option, value) =>
                                            option.label === value.label
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: renderInputFlag(
                                                        country?.value as string,
                                                    ),
                                                }}
                                                inputSize='m'
                                            />
                                        )}
                                        renderOption={renderFlagOption}
                                        options={countryOptions}
                                        getOptionDisabled={(opt) => {
                                            if (sdkOptions?.settings.countries?.length) {
                                                return sdkOptions?.settings.countries
                                                    ? !sdkOptions?.settings.countries?.includes(
                                                          opt.value,
                                                      )
                                                    : false;
                                            }

                                            return false;
                                        }}
                                    />
                                </LabeledField>
                            </div>
                        )}
                    </div>
                    {!isCameraMode &&
                        documentsStep !== DocumentsStepEnum.SELECT_COUNTRY &&
                        !isLoading && (
                            <div className='actions'>
                                {!!continueText && (
                                    <Button
                                        variant={isError ? 'error' : 'primary'}
                                        onClick={handleNextClick}
                                        size='xl'
                                        className={cn('button', {
                                            'full-width-button': !isShowUploadButton,
                                        })}
                                    >
                                        {continueText}
                                    </Button>
                                )}
                                {isShowUploadButton && (
                                    <React.Fragment>
                                        <input
                                            ref={fileInputRef}
                                            type='file'
                                            name='image'
                                            accept='image/png,image/jpg,image/jpeg'
                                            multiple={false}
                                            onChange={handleFileUploadChange}
                                            className='hidden'
                                        />

                                        <Button
                                            variant={uploadButtonVariant}
                                            onClick={handleUploadFileClick}
                                            size='xl'
                                            className={cn('button upload-file', {
                                                'full-width-button': !continueText,
                                            })}
                                            disabled={isLoading}
                                        >
                                            {t('common.uploadFile')}
                                        </Button>
                                    </React.Fragment>
                                )}
                            </div>
                        )}
                    {documentsStep === DocumentsStepEnum.SELECT_COUNTRY && (
                        <div className='actions'>
                            <Button
                                variant='secondary'
                                className='button'
                                onClick={handleCancelClick}
                                size='xl'
                                disabled={isSetCountryLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant='primary'
                                disabled={!country?.value}
                                onClick={handleSelectCountryNextClick}
                                size='xl'
                                className='button'
                                loading={isSetCountryLoading}
                            >
                                {t('common.continue')}
                            </Button>
                        </div>
                    )}
                </React.Fragment>
            )}
        </DocumentsContainer>
    );
};
